import { Injectable } from '@angular/core';
import { Observable, of, throwError, interval, Subscription } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DataService } from "../../core/services/data.service";
import { AppUtil } from '../../app.util';
import { ToastrService } from 'ngx-toastr';
export interface VersionInfo {
  appVersionNumber: string;
  appReleaseDate: string;
  environment: string;
  buildNumber?: number;
  gitHash?: string;
  gitBranch?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiVersionService {
  private readonly checkInterval = 0.50 * 60 * 1000; // 5 minutes
  private readonly timeoutMs = 10000; // 10 seconds
  private checkIntervalId: any;
  private versionCheckSubscription: Subscription | null = null;
  private updateListener: any;
  private increaseIntervalTimes: number = 1;
  constructor(
    private toastr: ToastrService,
    private dataService: DataService,
  ) {
  }

  /**
   * Initialize version checking with all features
   */
  public initVersionCheck(): void {
    this.checkVersion();
    this.checkIntervalId = setInterval(() => {
      this.checkVersion();
    }, this.checkInterval * this.increaseIntervalTimes);
  }

  /**
   * Check version via API with fallback
   */
  private checkVersion(): void {

    this.getServerVersion().subscribe({
      next: (response) => {
        if (response.result) {
          this.handleVersionUpdate(response.data);
        } else {
          this.toastr.error("Please call support team to update that you might not get access the application latest version");
          console.error('API version check failed:', response.error);
        }
      },
      error: (error) => {
        console.warn('API version check failed, trying static file:', error);
      }
    });
  }

  /**
   * Get version from server API endpoint
   */
  private getServerVersion(): Observable<any> {
    return this.dataService.getAll<any>(`AppVersion/GetActiveAppVersionsByAppName?appName=MFG-UI`)
      .pipe(
        timeout(this.timeoutMs),
        catchError(error => {
          console.warn('API version endpoint not available');
          return throwError(error);
        })
      );
  }

  /**
   * Handle version update detection (internal method)
   */
  private handleVersionUpdate(versionInfo: VersionInfo): void {
    const storageVersion = AppStorageUtil.getItem("appVersion");
    
    // First time initialization - just store the version without notification
    if (AppUtil.isEmptyString(storageVersion)) {
      AppStorageUtil.setItem("appVersion", versionInfo.appVersionNumber.toString());
      AppStorageUtil.setItem("buildTime", versionInfo.appReleaseDate.toString());
      return;
    }
  
    // Check for version mismatch
    if (storageVersion.toString() !== versionInfo.appVersionNumber.toString()) {
      // Trigger update notification
      const updateEvent = new CustomEvent('versionUpdate', {
        detail: {
          versionInfo: versionInfo
        }
      });
      window.dispatchEvent(updateEvent);
      
      // Update stored version after notification
      AppStorageUtil.setItem("appVersion", versionInfo.appVersionNumber.toString());
      AppStorageUtil.setItem("buildTime", versionInfo.appReleaseDate.toString());
      
      // Increase check interval
      this.increaseIntervalTimes = 2;
    }
  }
  

  /**
   * Stop version checking
   */
  public stopVersionChecking(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    this.versionCheckSubscription?.unsubscribe();
    if (this.updateListener) {
      window.removeEventListener('versionUpdate', this.updateListener);
    }
  }
} 